import React from 'react';

export const ObservationEntryTable = ({
  testId,
  observations = [],
  instrumentResultsMap = {},
  onObservationChange,
  onRemarksChange,
  onFillDefaults,
  onOpenParameterHistory,
  readOnly = false,
}) => {
  if (!observations || observations.length === 0) {
    return (
      <div className="path-empty-obs">
        <p>No observation parameters configured for this investigation.</p>
      </div>
    );
  }

  const renderFlagBadge = (flag) => {
    switch (flag) {
      case 'LL':
        return <span className="path-flag-badge flag-critical-low" title="Critical Low (Panic Value)">🔻🔻 CRIT LOW</span>;
      case 'HH':
        return <span className="path-flag-badge flag-critical-high" title="Critical High (Panic Value)">🔺🔺 CRIT HIGH</span>;
      case 'L':
        return <span className="path-flag-badge flag-low" title="Below Normal Range">🔻 LOW</span>;
      case 'H':
        return <span className="path-flag-badge flag-high" title="Above Normal Range">🔺 HIGH</span>;
      case 'A':
        return <span className="path-flag-badge flag-abnormal" title="Abnormal Finding">⚠️ ABNORMAL</span>;
      case 'N':
      default:
        return <span className="path-flag-badge flag-normal">NORMAL</span>;
    }
  };

  const renderDeltaIndicator = (obs) => {
    if (!obs.previousValue) return null;

    if (obs.deltaStatus === 'DELTA_ALERT' || obs.deltaStatus === 'CRITICAL' || obs.deltaStatus === 'QUALITATIVE_CHANGE') {
      return (
        <button
          type="button"
          className="path-delta-badge badge-delta-alert"
          onClick={() => onOpenParameterHistory && onOpenParameterHistory(obs, testId)}
          title={`Delta Alert: Previous was ${obs.previousValue} (${obs.deltaPercentChange !== undefined && obs.deltaPercentChange !== null ? `${obs.deltaPercentChange}%` : 'Shifted'}). Click for history.`}
        >
          ⚠ Δ
        </button>
      );
    }

    return (
      <button
        type="button"
        className="path-delta-badge badge-delta-norm"
        onClick={() => onOpenParameterHistory && onOpenParameterHistory(obs, testId)}
        title={`Delta Normal: Previous was ${obs.previousValue}. Click for history.`}
      >
        ✓
      </button>
    );
  };

  const renderValueInput = (obs) => {
    const isOptionFormat = obs.readingFormat?.toLowerCase() === 'option';
    const isPosNeg = obs.readingFormat?.toLowerCase() === 'positive/negative';

    if (isOptionFormat && obs.options && obs.options.length > 0) {
      return (
        <select
          className="path-obs-select"
          value={obs.currentValue || ''}
          onChange={(e) => onObservationChange(testId, obs.labObservationId, e.target.value)}
          disabled={readOnly}
        >
          <option value="">-- Select Option --</option>
          {obs.options.map((opt) => (
            <option key={opt.id || opt.text} value={opt.text}>
              {opt.text}
            </option>
          ))}
        </select>
      );
    }

    if (isPosNeg) {
      return (
        <select
          className="path-obs-select"
          value={obs.currentValue || ''}
          onChange={(e) => onObservationChange(testId, obs.labObservationId, e.target.value)}
          disabled={readOnly}
        >
          <option value="">-- Select --</option>
          <option value="Negative">Negative</option>
          <option value="Positive">Positive</option>
          <option value="Equivocal">Equivocal</option>
          <option value="Borderline">Borderline</option>
        </select>
      );
    }

    return (
      <input
        type={obs.readingFormat?.toLowerCase() === 'numeric' ? 'number' : 'text'}
        step="any"
        className={`path-obs-input ${obs.isCritical ? 'input-critical' : obs.currentFlag === 'H' || obs.currentFlag === 'L' ? 'input-abnormal' : ''}`}
        placeholder={obs.defaultValue ? `Default: ${obs.defaultValue}` : 'Enter result...'}
        value={obs.currentValue !== undefined && obs.currentValue !== null ? obs.currentValue : ''}
        onChange={(e) => onObservationChange(testId, obs.labObservationId, e.target.value)}
        disabled={readOnly}
      />
    );
  };

  const formatRefRange = (obs) => {
    if (obs.displayReading) return obs.displayReading;
    if (obs.minReading && obs.maxReading) return `${obs.minReading} - ${obs.maxReading}`;
    if (obs.minReading) return `> ${obs.minReading}`;
    if (obs.maxReading) return `< ${obs.maxReading}`;
    return '—';
  };

  return (
    <div className="path-obs-table-container">
      {onFillDefaults && !readOnly && (
        <div className="path-obs-table-actions">
          <button
            type="button"
            className="path-btn path-btn-sm path-btn-secondary"
            onClick={() => onFillDefaults(testId)}
            title="Populate empty fields with reference normal or default values"
          >
            ⚡ Auto-Fill Normal Values
          </button>
        </div>
      )}

      <table className="path-obs-table">
        <thead>
          <tr>
            <th style={{ width: '35px' }}>#</th>
            <th style={{ width: '22%' }}>Parameter / Observation</th>
            <th style={{ width: '22%' }}>Result / Value</th>
            <th style={{ width: '12%' }}>Flag</th>
            <th style={{ width: '10%' }}>Unit</th>
            <th style={{ width: '15%' }}>Reference Range</th>
            <th style={{ width: '9%' }}>Machine / Def</th>
            <th style={{ width: '10%' }}>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {observations.map((obs, idx) => {
            const isAbnormalRow = obs.currentFlag === 'H' || obs.currentFlag === 'L' || obs.currentFlag === 'A';
            const isCriticalRow = obs.currentFlag === 'LL' || obs.currentFlag === 'HH';

            return (
              <tr
                key={obs.labObservationId || idx}
                className={`path-obs-row ${isCriticalRow ? 'row-critical' : isAbnormalRow ? 'row-abnormal' : ''}`}
              >
                <td className="path-cell-muted">{idx + 1}</td>
                <td>
                  <div
                    className="path-param-name clickable-param"
                    onClick={() => onOpenParameterHistory && onOpenParameterHistory(obs, testId)}
                    title="Click to view parameter history & trend"
                  >
                    <span className={obs.isBold ? 'fw-bold' : ''}>{obs.name}</span>
                    {obs.resultRequired && <span className="path-req-star">*</span>}
                  </div>
                  {obs.method && <div className="path-param-method">Method: {obs.method}</div>}
                </td>
                <td>
                  <div className="path-obs-val-wrapper">
                    {renderValueInput(obs)}
                    {renderDeltaIndicator(obs)}
                    <button
                      type="button"
                      className="path-btn-history-trend"
                      onClick={() => onOpenParameterHistory && onOpenParameterHistory(obs, testId)}
                      title="Parameter History & Delta Check"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </button>
                  </div>
                </td>
                <td>{renderFlagBadge(obs.currentFlag)}</td>
                <td>
                  <span className="path-unit-text">{obs.unit || '—'}</span>
                </td>
                <td>
                  <span className="path-range-text">{formatRefRange(obs)}</span>
                </td>
                <td>
                  {(() => {
                    const machineRes = Array.isArray(instrumentResultsMap)
                      ? instrumentResultsMap.find(r => String(r.limsObservationId) === String(obs.labObservationId) || r.limsObservationNm === obs.name)
                      : (instrumentResultsMap?.[obs.labObservationId] || instrumentResultsMap?.[obs.name]);

                    if (machineRes) {
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: '#e0f2fe',
                              color: '#0369a1',
                              fontWeight: '700',
                              fontSize: '0.72rem',
                              cursor: 'help',
                              border: '1px solid #bae6fd',
                              width: 'fit-content'
                            }}
                            title={`Origin: ${machineRes.analyzerName || machineRes.analyzerCode}\nRaw Machine Value: ${machineRes.machineRawValue}\nNormalized Value: ${machineRes.normalizedValue || machineRes.machineRawValue}\nSync Status: ${machineRes.validationStatus || 'ACCEPTED'}\nSeverity: ${machineRes.validationSeverity || 'PASS'}${machineRes.createdAt ? `\nReceived: ${new Date(machineRes.createdAt).toLocaleTimeString()}` : ''}`}
                          >
                            <span>🤖</span>
                            <span>{machineRes.analyzerCode || 'ANALYZER'}</span>
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span title="Unedited physical raw value">Raw: <strong>{machineRes.machineRawValue}</strong></span>
                            {machineRes.isCritical && (
                              <span style={{ color: '#dc2626', fontWeight: 800, fontSize: '0.68rem' }} title="Critical Panic Breach">🚨 PANIC</span>
                            )}
                            {!machineRes.isCritical && (machineRes.validationSeverity === 'WARNING' || machineRes.validationSeverity === 'REVIEW_REQUIRED') && (
                              <span style={{ color: '#d97706', fontWeight: 700, fontSize: '0.68rem' }} title="Clinical Advisory">⚠️ {machineRes.validationSeverity}</span>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return <span className="path-cell-muted">{obs.defaultValue || '—'}</span>;
                  })()}
                </td>
                <td>
                  <input
                    type="text"
                    className="path-remarks-input"
                    placeholder="Notes..."
                    value={obs.currentRemarks || ''}
                    onChange={(e) => onRemarksChange(testId, obs.labObservationId, e.target.value)}
                    disabled={readOnly}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ObservationEntryTable;
